/**
 * ENCO Scientific quote PDF parser (operates on line-reconstructed text from pdfjs-dist).
 *
 * pdfjs often emits visually-RTL Hebrew with reversed label/value order
 * (e.g. "27247 : מחיר הצעת" instead of "הצעת מחיר: 27247").
 */

import { logInfo } from '../../../config_and_helpers/logger';
import { buildQuoteExtractionSnapshot } from '../logQuoteExtraction';
import { matchText, parseMoney, parseNumber } from './rheniumQuoteParserHelpers';

const LOG_CONTEXT = 'encoQuoteParser';

/** Optional colon (Hebrew/ASCII) with surrounding whitespace. */
const LABEL_SEP = '\\s*[:：]?\\s*';

const SHEKEL_MARK = "ח\\s*(?:''|['''״]\\s*)?\\s*ש";

const ENCO_PRICE_LINE_LTR_RE =
  /(\d+(?:\.\d+)?)\s*יח['׳"]?\s+([\d,.]+)\s+USD\s+([\d,.]+)\s+([\d,.]+)\s*(?:ש["״']?ח|₪|ILS)/i;

const ENCO_PRICE_LINE_RTL_RE = new RegExp(
  `${SHEKEL_MARK}\\s+([\\d,.]+)\\s+([\\d,.]+)\\s+USD\\s+([\\d,.]+)\\s+['''״]?\\s*יח\\s+(\\d+(?:\\.\\d+)?)`,
  'i'
);

/** Full ENCO catalog numbers include a size suffix (e.g. HY-114277-5MG), not URL path fragments. */
const ENCO_PRODUCT_NUMBER_RE = /\b(HY-\d+(?:-[A-Z0-9]+)+)\b/i;

export const ENCO_PARSER_FORMAT_ID = 'enco_scientific_quote_pdf';

/**
 * @typedef {{ page?: number, y?: number, text: string }} QuotePdfLine
 */

/**
 * @param {string} text
 * @returns {boolean}
 */
export function isEncoQuote(text) {
  return [
    /ENCO Scientific/i,
    /Scientific Services LTD/i,
    /אנקו/,
    /orders@enco\.co\.il/i,
    /www\.enco\.co\.il/i,
    /511233454/,
    /הצעת[\s-]*מחיר\s*:\s*\d+/,
    /\d+\s*:\s*מחיר\s*הצעת/,
  ].some(pattern => pattern.test(text));
}

/**
 * @param {QuotePdfLine[]} lines
 * @param {string|null} [sourceFileName]
 * @returns {Object}
 */
export function extractEncoQuote(lines, sourceFileName = null) {
  const lineTexts = (lines || []).map(line => String(line.text || '').trim()).filter(Boolean);
  const text = normalizeText(lineTexts.join('\n'));

  if (!isEncoQuote(text)) {
    throw new Error('This PDF does not look like an ENCO quote');
  }

  const quote = extractQuoteHeader(text, lineTexts);
  const items = extractItems(lineTexts);
  const totals = extractTotals(text, lineTexts);
  const customer = extractCustomer(text, lineTexts);

  const result = {
    supplier: extractSupplier(text, lineTexts),
    supplierContact: extractSupplierContact(text, lineTexts),
    customer,
    deliveryAddress: extractDeliveryAddress(text, lineTexts),
    quote,
    items,
    totals,
    warnings: buildWarnings({ quote, items, totals }),
    meta: {
      sourceFileName,
      parserFormat: ENCO_PARSER_FORMAT_ID,
      parserVersion: 'enco-react-parser-v2-rtl',
      rawText: text,
      pdfLineCount: lineTexts.length,
    },
  };

  logInfo(LOG_CONTEXT, 'extractEncoQuote complete', {
    sourceFileName,
    quoteNumber: quote.quoteNumber,
    itemCount: items.length,
    total: totals.total,
    pdfLineCount: lineTexts.length,
    extracted: buildQuoteExtractionSnapshot(result),
  });

  return result;
}

/**
 * @param {string} text
 * @returns {string}
 */
function normalizeText(text) {
  return text
    .replace(/\r/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/**
 * Match `label: value` (LTR) or `value : label` (RTL pdfjs) across lines.
 *
 * @param {string} text
 * @param {string[]} lineTexts
 * @param {string} labelPattern - regex body for Hebrew label words
 * @param {string} valuePattern - regex body for captured value
 * @returns {string|null}
 */
function matchBilingualLabel(text, lineTexts, labelPattern, valuePattern) {
  for (const line of lineTexts) {
    const lineRtl = line.match(
      new RegExp(`^(${valuePattern})\\s*:\\s*${labelPattern}\\s*$`, 'i')
    );
    if (lineRtl) return lineRtl[1].trim();

    const lineLtr = line.match(
      new RegExp(`^${labelPattern}${LABEL_SEP}(${valuePattern})\\s*$`, 'i')
    );
    if (lineLtr) return lineLtr[1].trim();
  }

  const ltr = matchText(
    text,
    new RegExp(`${labelPattern}${LABEL_SEP}(${valuePattern})`, 'i'),
    1
  );
  if (ltr) return ltr;

  const rtl = matchText(
    text,
    new RegExp(`(${valuePattern})\\s*:\\s*${labelPattern}`, 'i'),
    1
  );
  if (rtl) return rtl;

  return findValueOnNextLine(lineTexts, new RegExp(labelPattern, 'i'), new RegExp(`^${valuePattern}$`, 'i'));
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object}
 */
function extractSupplier(text, lineTexts = []) {
  return {
    name: 'ENCO Scientific Services LTD',
    hebrewName: 'אנקו שירותי מדע בע"מ',
    companyId:
      matchBilingualLabel(text, lineTexts, 'פ\\s*\\.?\\s*ח', '\\d{9}') ||
      matchText(text, /ח\.פ:\s*(\d+)/, 1) ||
      '511233454',
    website: matchText(text, /(www\.enco\.co\.il)/i, 1),
    email: matchText(text, /(orders@enco\.co\.il)/i, 1),
    phone: matchText(text, /(972-3-9349922|\d{1,2}-\d{7})/, 1),
    fax: matchText(text, /(972-3-9349876)/, 1),
  };
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object}
 */
function extractSupplierContact(text, lineTexts = []) {
  const rawName =
    matchBilingualLabel(text, lineTexts, 'קשר\\s+איש\\s+שם', "[A-Za-z\\s'.]+") ||
    matchText(text, /שם איש קשר:\s*([^\n]+)/, 1) ||
    matchText(text, /(Yifat Ovadia)/i, 1);

  const encoEmails = text.match(/[A-Z0-9._%+-]+@enco\.co\.il/gi) || [];
  const contactEmail =
    encoEmails.find(email => !/^orders@enco\.co\.il$/i.test(email)) ||
    encoEmails[0] ||
    null;

  const mobileLine = lineTexts.find(line => /^\+?\d/.test(line) && /נייד|2463035/.test(line));
  const mobileFromLabel =
    matchBilingualLabel(text, lineTexts, 'נייד\\s+טלפון', '[+\\d\\s().-]+') ||
    matchText(text, /טלפון נייד:\s*([^\n]+)/, 1);

  return {
    name: normalizeContactName(rawName),
    phone: normalizePhone(mobileFromLabel || mobileLine || null),
    email: contactEmail,
  };
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object}
 */
const CUSTOMER_NUMBER_LABEL = '(?:מספר\\s+לקוח|לקוח\\s+מספר)';

function extractCustomer(text, lineTexts = []) {
  const customerNumber = normalizeEncoCustomerNumber(
    matchBilingualLabel(text, lineTexts, CUSTOMER_NUMBER_LABEL, '[A-Z0-9]+')
  );

  const contactPerson =
    matchBilingualLabel(text, lineTexts, 'לידי', "[^:\\n]+") ||
    matchText(text, new RegExp(`לידי${LABEL_SEP}([^\\n]+)`), 1);

  const phoneRaw =
    matchBilingualLabel(text, lineTexts, 'טלפון', '[+\\d\\s().-]+') ||
    matchText(text, new RegExp(`טלפון${LABEL_SEP}([^\\n]+)`), 1);

  return {
    name:
      matchText(text, /קרן מחקרים-[^\n]+/, 0) ||
      matchText(text, /קרן\s+מחקרים[^\n]*/i, 0) ||
      matchText(text, /לכבוד:\s*([^\n]+)/, 1) ||
      lineTexts.find(line => /מחקרים\s+קרן|קרן\s+מחקרים/.test(line)) ||
      null,
    departmentOrLab:
      matchText(text, /ד"ר מרים גרינוולד - הדסה/, 0) ||
      lineTexts.find(line => /גרינוולד\s+מרים|מרים\s+גרינוולד/.test(line)) ||
      null,
    contactPerson: contactPerson ? contactPerson.trim() : null,
    phone: normalizePhone(phoneRaw),
    email: (() => {
      const emails = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
      return emails.find(email => !/@enco\.co\.il$/i.test(email)) || null;
    })(),
    customerNumber,
  };
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object|null}
 */
function extractDeliveryAddress(text, lineTexts = []) {
  let block = matchText(
    text,
    /כתובת אספקה:\n([\s\S]*?)\nאנו מודים לך/,
    1
  );

  if (!block) {
    block = matchText(
      text,
      new RegExp(`כתובת אספקה${LABEL_SEP}([\\s\\S]*?)(?:\\nאנו מודים|$)`),
      1
    );
  }

  if (!block) {
    const startIndex = lineTexts.findIndex(
      line => /כתובת אספקה|אספקה\s+כתובת/.test(line)
    );
    if (startIndex >= 0) {
      const collected = [];
      for (let i = startIndex + 1; i < lineTexts.length; i += 1) {
        const line = lineTexts[i];
        if (/אנו מודים|מודים לך|כמפורט הצעתנו|שורה\s+מוצר/.test(line)) break;
        if (/קשר איש|נייד טלפון|@/.test(line)) continue;
        collected.push(line);
        if (collected.length >= 6) break;
      }
      if (collected.length) {
        block = collected.join('\n');
      }
    }
  }

  if (!block) {
    return null;
  }

  const addressLines = block
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  return {
    recipient: addressLines[0] || null,
    location: addressLines[1] || null,
    buildingOrStreet: addressLines[2] || null,
    city: extractCity(addressLines[addressLines.length - 1]),
    postalCode: extractPostalCode(addressLines[addressLines.length - 1]),
    rawAddress: addressLines.join(', '),
  };
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object}
 */
function extractQuoteHeader(text, lineTexts = []) {
  const quoteNumber = matchBilingualLabel(
    text,
    lineTexts,
    '(?:מחיר\\s*הצעת|הצעת[\\s-]*מחיר)',
    '\\d+'
  );

  const quoteDate = matchBilingualLabel(
    text,
    lineTexts,
    '(?:הצעה\\s+תאריך|תאריך\\s+הצעה)',
    '\\d{2}\\/\\d{2}\\/\\d{4}'
  );

  const validUntil =
    matchBilingualLabel(
      text,
      lineTexts,
      '(?:ההצעה\\s+תוקף|תוקף\\s+ההצעה)',
      '\\d{2}\\/\\d{2}\\/\\d{4}'
    ) || matchText(text, /(\d{2}\/\d{2}\/\d{4})\s*:\s*ההצעה\s+תוקף/, 1);

  const customerNumber = normalizeEncoCustomerNumber(
    matchBilingualLabel(text, lineTexts, CUSTOMER_NUMBER_LABEL, '[A-Z0-9]+')
  );

  const customerOrderRaw = matchBilingualLabel(
    text,
    lineTexts,
    'הזמנתכם',
    "[^:\\n]+"
  );
  const customerOrderNumber =
    customerOrderRaw &&
    !/^:\s*$/.test(customerOrderRaw.trim()) &&
    !/(טלפון|@|\+?\d{2,})/.test(customerOrderRaw)
      ? customerOrderRaw.trim()
      : null;

  const paymentTerms =
    matchBilingualLabel(
      text,
      lineTexts,
      '(?:תשלום\\s+תנאי|תנאי\\s+תשלום)',
      '.+'
    ) || matchText(text, /(\d+\+\s*שוטף)\s*:\s*תשלום\s+תנאי/, 1);

  const note =
    matchText(text, /(המחיר צמוד לשער מטבע זר ביום הוצאת החשבונית)/, 1) ||
    matchText(
      text,
      /(המחיר\s+צמוד\s+לשער\s+מטבע\s+זר\s+ביום\s+הוצאת\s+החשבונית)/,
      1
    ) ||
    lineTexts.find(line => /צמוד\s+המחיר|המחיר\s+צמוד/.test(line)) ||
    null;

  return {
    quoteNumber,
    quoteDate,
    validUntil,
    customerOrderNumber,
    customerNumber,
    paymentTerms: paymentTerms ? paymentTerms.trim() : null,
    currency: 'ILS',
    note,
  };
}

/**
 * @param {string} line
 * @returns {{ quantity: number|null, unitPrice: number|null, discountPercent: number|null, lineTotal: number|null }|null}
 */
function parseEncoPriceLine(line) {
  const ltr = line.match(ENCO_PRICE_LINE_LTR_RE);
  if (ltr) {
    return {
      quantity: parseNumber(ltr[1]),
      unitPrice: parseMoney(ltr[2]),
      discountPercent: parseNumber(ltr[3]),
      lineTotal: parseMoney(ltr[4]),
    };
  }

  const rtl = line.match(ENCO_PRICE_LINE_RTL_RE);
  if (rtl) {
    return {
      lineTotal: parseMoney(rtl[1]),
      discountPercent: parseNumber(rtl[2]),
      unitPrice: parseMoney(rtl[3]),
      quantity: parseNumber(rtl[4]),
    };
  }

  return null;
}

/**
 * @param {string[]} textLines
 * @returns {Object[]}
 */
function extractItems(textLines) {
  /** @type {Object[]} */
  const items = [];
  const seenProducts = new Set();

  const pushItem = (productNumber, priceLine, context) => {
    const price = parseEncoPriceLine(priceLine);
    if (!price || price.quantity == null) return false;
    if (seenProducts.has(productNumber.toUpperCase())) return false;

    seenProducts.add(productNumber.toUpperCase());
    items.push({
      lineNumber: extractLineNumber(context, productNumber),
      productNumber,
      catalogNumber: productNumber,
      description: extractDescription(priceLine, context, productNumber),
      casNumber: matchText(context, /\((\d{2,7}-\d{2}-\d)\)/, 1),
      quantity: price.quantity,
      unit: "יח'",
      unitPrice: price.unitPrice,
      unitPriceCurrency: 'USD',
      discountPercent: price.discountPercent,
      lineTotal: price.lineTotal,
      lineTotalCurrency: 'ILS',
      productDataSheetUrl: matchText(context, /(https?:\/\/\S+)/, 1),
      rawLine: priceLine,
      rawContext: context,
    });
    return true;
  };

  for (let index = 0; index < textLines.length; index += 1) {
    const productNumber = findEncoProductNumberOnLine(textLines[index]);
    if (!productNumber) continue;

    const contextStart = Math.max(0, index - 4);
    const contextEnd = Math.min(textLines.length, index + 5);
    const contextLines = textLines.slice(contextStart, contextEnd);
    const context = contextLines.join(' ');
    const anchorIndex = index - contextStart;
    const priceLine =
      findClosestPriceLine(contextLines, anchorIndex) ||
      (isLikelyEncoPriceLine(textLines[index]) ? textLines[index] : '');

    if (priceLine) {
      pushItem(productNumber, priceLine, context);
    }
  }

  if (!items.length) {
    for (let index = 0; index < textLines.length; index += 1) {
      const currentLine = textLines[index];
      if (!isLikelyEncoPriceLine(currentLine)) continue;

      const contextLines = textLines.slice(index, index + 4).filter(Boolean);
      const context = contextLines.join(' ');
      const productNumber = findEncoProductNumberInText(context);
      if (!productNumber) continue;

      pushItem(productNumber, currentLine, context);
    }
  }

  return items;
}

/**
 * @param {string} line
 * @returns {string|null}
 */
/**
 * @param {string[]} contextLines
 * @param {number} anchorIndex
 * @returns {string}
 */
function findClosestPriceLine(contextLines, anchorIndex) {
  let bestLine = '';
  let bestDistance = Infinity;

  contextLines.forEach((line, lineIndex) => {
    if (!isLikelyEncoPriceLine(line)) return;
    const distance = Math.abs(lineIndex - anchorIndex);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestLine = line;
    }
  });

  return bestLine;
}

function findEncoProductNumberOnLine(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed || /^https?:\/\//i.test(trimmed)) return null;
  const match = trimmed.match(ENCO_PRODUCT_NUMBER_RE);
  return match ? match[1] : null;
}

/**
 * @param {string} text
 * @returns {string|null}
 */
function findEncoProductNumberInText(text) {
  const matches =
    String(text || '').match(new RegExp(ENCO_PRODUCT_NUMBER_RE.source, 'gi')) || [];
  if (!matches.length) return null;
  return matches.sort((a, b) => b.length - a.length)[0];
}

function isLikelyEncoPriceLine(line) {
  if (!/USD/i.test(line) || !/יח/.test(line)) return false;
  if (parseEncoPriceLine(line)) return true;
  return (
    new RegExp(SHEKEL_MARK, 'i').test(line) &&
    (/(\d+(?:\.\d+)?)\s*יח/.test(line) || /יח\s+\d+/.test(line))
  );
}

/**
 * @param {string[]} lineTexts
 * @param {RegExp} labelPattern
 * @param {RegExp} [valuePattern]
 * @returns {string|null}
 */
function findValueOnNextLine(lineTexts, labelPattern, valuePattern = /.+/) {
  for (let i = 0; i < lineTexts.length - 1; i += 1) {
    if (!labelPattern.test(lineTexts[i])) continue;
    const inline = lineTexts[i].replace(labelPattern, '').replace(/^:\s*/, '').trim();
    if (inline && valuePattern.test(inline)) return inline;
    const next = lineTexts[i + 1]?.trim();
    if (next && valuePattern.test(next)) return next;
  }
  return null;
}

/**
 * @param {string} context
 * @param {string} productNumber
 * @returns {number|null}
 */
function extractLineNumber(context, productNumber) {
  const afterProduct = context.match(
    new RegExp(`${escapeRegExp(productNumber)}\\s+(\\d+)`, 'i')
  );

  if (afterProduct) {
    return parseNumber(afterProduct[1]);
  }

  const beforeProduct = context.match(
    new RegExp(`(?:^|\\s)(\\d+)\\s+${escapeRegExp(productNumber)}`, 'i')
  );

  if (beforeProduct) {
    return parseNumber(beforeProduct[1]);
  }

  return null;
}

/**
 * @param {string} priceLine
 * @param {string} context
 * @param {string} productNumber
 * @returns {string|null}
 */
function extractDescription(priceLine, context, productNumber) {
  let description = null;

  const rtlDesc = priceLine.match(
    new RegExp(
      `['''״]?\\s*יח\\s+\\d+(?:\\.\\d+)?\\s+(.+?)\\s+${escapeRegExp(productNumber)}`,
      'i'
    )
  );
  if (rtlDesc) {
    description = rtlDesc[1];
  }

  const beforeQuantity = priceLine.match(/^\s*(.*?)\s+\d+(?:\.\d+)?\s*יח/);
  if (!description && beforeQuantity) {
    description = beforeQuantity[1];
  }

  if (!description && productNumber) {
    const afterProduct = context.match(
      new RegExp(
        `${escapeRegExp(productNumber)}\\s+(.*?)\\s+\\d+(?:\\.\\d+)?\\s*יח`,
        'i'
      )
    );

    if (afterProduct) {
      description = afterProduct[1];
    }
  }

  return normalizeEncoDescription(description);
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function normalizeEncoDescription(value) {
  if (!value) return null;

  return value
    .replace(/\bmg(\d+)\b/gi, '$1mg')
    .replace(/(\d+)mg\b/gi, '$1mg')
    .replace(/(\d+)\s+mg\b/gi, '$1mg')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * @param {string} text
 * @param {string[]} lineTexts
 * @returns {Object}
 */
function extractTotals(text, lineTexts = []) {
  const shekelSuffix = '(?:ש["״\']?ח|₪|ILS)';

  let subtotalBeforeVat = parseMoney(
    matchText(
      text,
      new RegExp(`סך הכל לפני מע['׳"]{0,2}מ\\s+([\\d,.]+)\\s*${shekelSuffix}`),
      1
    )
  );

  if (subtotalBeforeVat == null) {
    subtotalBeforeVat = parseMoney(
      matchText(
        text,
        new RegExp(
          `${SHEKEL_MARK}\\s+([\\d,.]+)\\s+מ\\s*(?:''|['''״]\\s*)?\\s*מע\\s+לפני\\s+הכל\\s+סך`
        ),
        1
      )
    );
  }

  let vatRate = parseNumber(
    matchText(text, /מס ערך מוסף\s*\(%\)\s+([\d,.]+)/, 1)
  );
  let vatAmount = parseMoney(
    matchText(
      text,
      new RegExp(`מס ערך מוסף\\s*\\(%\\)\\s+[\\d,.]+\\s+([\\d,.]+)\\s*${shekelSuffix}`),
      1
    )
  );

  const vatLabelIndex = lineTexts.findIndex(line => /מוסף\s+ערך\s+מס/.test(line));
  if (vatLabelIndex > 0) {
    const rtlVatLine = lineTexts[vatLabelIndex - 1];
    const rtlVat = rtlVatLine?.match(
      new RegExp(`${SHEKEL_MARK}\\s+([\\d,.]+)\\s+([\\d,.]+)`)
    );
    if (rtlVat) {
      vatAmount = vatAmount ?? parseMoney(rtlVat[1]);
      vatRate = vatRate ?? parseNumber(rtlVat[2]);
    }
  }

  if (vatRate == null || vatAmount == null) {
    const rtlVatLine = lineTexts.find(
      line =>
        new RegExp(`${SHEKEL_MARK}\\s+[\\d,.]+\\s+[\\d,.]+`).test(line) &&
        !/USD|HY-/i.test(line) &&
        !/מע\s+לפני|הכול\s+סך/.test(line)
    );
    const rtlVat = rtlVatLine?.match(
      new RegExp(`${SHEKEL_MARK}\\s+([\\d,.]+)\\s+([\\d,.]+)`)
    );
    if (rtlVat) {
      vatAmount = vatAmount ?? parseMoney(rtlVat[1]);
      vatRate = vatRate ?? parseNumber(rtlVat[2]);
    }
  }

  let total = parseMoney(
    matchText(text, new RegExp(`סך הכול\\s+([\\d,.]+)\\s*${shekelSuffix}`), 1)
  );
  if (total == null) {
    total = parseMoney(
      matchText(text, new RegExp(`${SHEKEL_MARK}\\s+([\\d,.]+)\\s+הכול\\s+סך`), 1)
    );
  }

  return {
    subtotalBeforeVat,
    vatRate,
    vatAmount,
    total,
    currency: 'ILS',
  };
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function normalizeEncoCustomerNumber(value) {
  if (!value) return null;

  const clean = value.trim();
  const reversed = clean.match(/^(\d+)([A-Z])$/i);

  if (reversed) {
    return `${reversed[2].toUpperCase()}${reversed[1]}`;
  }

  return clean;
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function normalizeContactName(value) {
  if (!value) return null;

  if (/^Ovadia Yifat$/i.test(value.trim())) {
    return 'Yifat Ovadia';
  }

  return value.trim();
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function normalizePhone(value) {
  if (!value) return null;

  const clean = value.trim();

  if (/2463035\s*\(54\)\s*\+972/.test(clean) || /\+972\s*\(54\)\s*2463035/.test(clean)) {
    return '+972 (54) 2463035';
  }

  if (/55-6613811\s*\+972/.test(clean) || /\+972\s*55-6613811/.test(clean)) {
    return '+972 55-6613811';
  }

  return clean;
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function extractCity(value) {
  if (!value) return null;

  return value.replace(/\d+/g, '').trim() || null;
}

/**
 * @param {string|null} value
 * @returns {string|null}
 */
function extractPostalCode(value) {
  if (!value) return null;

  return matchText(value, /(\d{5,})/, 1);
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * @param {{ quote: Object, items: Object[], totals: Object }} params
 * @returns {Array<{ field: string, message: string, severity: string }>}
 */
function buildWarnings({ quote, items, totals }) {
  const warnings = [];

  if (!quote.quoteNumber) {
    warnings.push({
      field: 'quote.quoteNumber',
      message: 'Quote number could not be detected.',
      severity: 'warning',
    });
  }

  if (!items.length) {
    warnings.push({
      field: 'items',
      message: 'No ENCO quote items were detected.',
      severity: 'error',
    });
  }

  if (totals.subtotalBeforeVat && totals.vatRate && totals.vatAmount) {
    const expectedVat =
      Math.round(totals.subtotalBeforeVat * totals.vatRate) / 100;

    if (Math.abs(expectedVat - totals.vatAmount) > 0.1) {
      warnings.push({
        field: 'totals.vatAmount',
        message: 'VAT amount may not match subtotal × VAT rate.',
        severity: 'warning',
      });
    }
  }

  return warnings;
}
